import React, { useCallback, useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import cx from "classnames";

import { KeyboardNavigation } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { JumpToContext } from "~/search/JumpTo/JumpToContext";
import { Button } from "~/ui";

import styles from "./JumpToResult.module.css";

interface JumpToResultWrapperProps {
	icon: React.ReactElement;
	title: string | React.ReactElement;
	to?: string;
	onSelect?(): void;
	endAlignedElement?: React.ReactElement;
	index?: number;
}

export default function JumpToResultWrapper({
	icon,
	title,
	to,
	endAlignedElement,
	onSelect,
	index,
}: JumpToResultWrapperProps) {
	const [focused, setFocused] = useState(false);
	const history = useHistory();
	const { onClose } = useContext(JumpToContext);

	const focus = useCallback(() => setFocused(true), []);
	const unfocus = useCallback(() => setFocused(false), []);
	const navigate = useCallback(
		(e: Event | React.SyntheticEvent | null) => {
			if (!to) return;
			e?.preventDefault();
			history.push(to);
			onClose();
			let payload = index !== undefined ? { feature: "search", to, index } : { feature: "search", to };
			dispatchAnalyticsTrack("Jump to on select item", payload);
		},
		[history, to, onClose, index],
	);

	const resultContent = (
		<>
			<div className={styles.icon}>{icon}</div>
			{title}
			{endAlignedElement && <div className={styles.endAligned}>{endAlignedElement}</div>}
		</>
	);

	return (
		<KeyboardNavigation.Focusable onFocus={focus} onBlur={unfocus} onEnterDown={to ? navigate : onSelect}>
			{to ? (
				<Link to={to} className={cx(styles.wrapper, { [styles.focused]: focused })} onClick={navigate}>
					{resultContent}
				</Link>
			) : (
				<Button className={cx(styles.wrapper, { [styles.focused]: focused })} onClick={onSelect}>
					{resultContent}
				</Button>
			)}
		</KeyboardNavigation.Focusable>
	);
}
